/**
 * API helpers for user favorite league ids (stored in User.favoriteLeagueIds).
 * GET /users/profile/:userId returns profile.favoriteLeagueIds; PUT /users/profile updates it.
 */

import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants';

export type ApiClientFn = (endpoint: string, config?: unknown) => Promise<AxiosResponse>;

function parseFavoriteLeagueIdsFromProfile(data: unknown): string[] {
  const ids = (data as { data?: { profile?: { favoriteLeagueIds?: unknown } } })?.data?.profile
    ?.favoriteLeagueIds;
  if (!Array.isArray(ids)) return [];
  return ids.filter((id: unknown): id is string => typeof id === 'string');
}

/**
 * Fetch current user's favorite league ids from profile.
 * Returns empty array on error or when not authenticated.
 */
export async function fetchFavoriteLeagueIds(
  userId: string,
  apiClientFn: ApiClientFn
): Promise<string[]> {
  if (!userId || !apiClientFn) return [];
  const endpoint = `${API_ENDPOINTS.users.profile}/${userId}`;
  try {
    const response = await apiClientFn(endpoint);
    if (response.status >= 400) return [];
    return parseFavoriteLeagueIdsFromProfile(response.data);
  } catch {
    return [];
  }
}

/**
 * Update current user's favorite league ids via PUT /users/profile.
 * Returns the new array from the response, or the same array on error.
 */
export async function updateFavoriteLeagueIds(
  favoriteLeagueIds: string[],
  apiClientFn: ApiClientFn
): Promise<string[]> {
  if (!apiClientFn) return favoriteLeagueIds;
  try {
    const response = await apiClientFn(API_ENDPOINTS.users.profile, {
      method: 'PUT',
      data: { favoriteLeagueIds },
    });
    if (response.status >= 400) return favoriteLeagueIds;
    return parseFavoriteLeagueIdsFromProfile(response.data);
  } catch {
    return favoriteLeagueIds;
  }
}
